import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Mail, Lock, Eye, EyeOff, AlertCircle, Scale } from 'lucide-react';
import { APP_VERSION, BUILD_ID } from '../../utils/version';
import axios from 'axios';
import { goBack } from '../../utils/navigation';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function LoginAuxiliaresPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [inscripcionesAbiertas, setInscripcionesAbiertas] = useState(null);
  const [estadoConvocatoria, setEstadoConvocatoria] = useState(null);
  const [otrosDistritosHabilitado, setOtrosDistritosHabilitado] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Verificar si las inscripciones están abiertas
  useEffect(() => {
    const verificarEstado = async () => {
      try {
        const res = await axios.get(`${API_URL}/auxiliares-justicia/estado-inscripcion`);
        setInscripcionesAbiertas(res.data.inscripciones_abiertas);
        setEstadoConvocatoria(res.data);
        setOtrosDistritosHabilitado(Boolean(res.data.inscripciones_otros_distritos_abiertas));
      } catch (err) {
        console.error('Error verificando estado:', err);
        setInscripcionesAbiertas(false);
      } finally {
        setCheckingStatus(false);
      }
    };
    verificarEstado();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      // Siempre redirigir al formulario de auxiliares
      navigate('/auxiliares-justicia/formulario', { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.message) {
        setError(detail.message);
      } else if (typeof detail === 'string') {
        setError(detail);
      } else {
        setError('Error al iniciar sesión');
      }
    } finally {
      setLoading(false);
    }
  };

  // Verificando estado
  if (checkingStatus) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  // Inscripciones cerradas
  if (inscripcionesAbiertas === false) {
    // iter184: copy unificada con AuxiliaresJusticiaPage
    const anioActuacion = Number(estadoConvocatoria?.anio_actuacion) || null;
    const anioProxCiclo = anioActuacion ? anioActuacion + 1 : null;
    const fechaProxApertura = anioActuacion ? `01/09/${anioActuacion}` : null;

    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center py-8 px-4">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <img
              src="/logo-colegio.png"
              alt="Logo del Colegio"
              className="h-32 w-auto object-contain"
            />
          </div>

          <Card className="border-amber-200" data-testid="login-aux-banner-cerrada">
            <CardContent className="pt-6 text-center">
              <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
              <h2
                className="text-xl font-bold mb-3"
                data-testid="login-aux-banner-cerrada-titulo"
              >
                {anioActuacion
                  ? `Inscripción ${anioActuacion}: Convocatoria cerrada.`
                  : 'Convocatoria cerrada'}
              </h2>
              <p className="text-gray-700 mb-3">
                Por disposición de la SCBA la inscripción al listado de Auxiliares de Justicia se
                realiza durante el mes de septiembre de cada año.
              </p>
              {anioProxCiclo && fechaProxApertura && (
                <p
                  className="text-gray-700 mb-4"
                  data-testid="login-aux-banner-cerrada-proxima"
                >
                  Las inscripciones para el <strong>{anioProxCiclo}</strong> se abrirán nuevamente
                  el <strong>{fechaProxApertura}</strong>.
                </p>
              )}
              <Link to="/">
                <Button variant="outline" className="mt-2">
                  Volver al Inicio
                </Button>
              </Link>
            </CardContent>
          </Card>

          {/* iter186: Botón para matriculados de OTROS DISTRITOS. Se muestra
              independientemente del toggle general (depende solo del flag
              `inscripciones_otros_distritos_abiertas`). */}
          {otrosDistritosHabilitado && (
            <Card
              className="mt-5 border-amber-300 bg-amber-50"
              data-testid="card-otros-distritos-cerrada"
            >
              <CardContent className="pt-5 text-center">
                <p className="text-sm font-semibold text-amber-900 mb-3">
                  ¿Sos matriculadx de otro distrito?
                </p>
                <Link to="/auxiliares-justicia/otros-distritos" className="block">
                  <Button
                    className="w-full h-auto min-h-[48px] py-3 px-4 bg-amber-600 hover:bg-amber-700 text-white whitespace-normal break-words text-xs leading-snug text-center"
                    data-testid="btn-otros-distritos-ir"
                  >
                    Inscripción de matriculadxs de otros distritos a los Deptos. Judiciales
                    Lomas de Zamora / Avellaneda-Lanús
                  </Button>
                </Link>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center py-8 px-4 overflow-x-hidden">
      {/* Skip link para accesibilidad */}
      <a href="#login-form" className="skip-link">
        Ir al formulario de inicio de sesión
      </a>
      
      <div className="w-full max-w-md animate-fade-in">
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <img
            src="/logo-colegio.png"
            alt="Logo del Colegio de Trabajadorxs Sociales de Lomas de Zamora"
            className="h-32 sm:h-40 w-auto max-w-[200px] sm:max-w-none object-contain"
            data-testid="logo-colegio"
            loading="eager"
          />
        </div>

        {/* Descripción de la app */}
        <div className="text-center mb-6 px-2">
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-100 rounded-xl p-4 shadow-sm">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Scale className="w-5 h-5 text-amber-600" />
              <strong className="text-amber-700">Auxiliares de Justicia</strong>
            </div>
            <p className="text-sm text-gray-700 leading-relaxed">
              Inscripción al listado oficial según Acordada 2728 del 
              <strong> Colegio Profesional de Trabajadorxs Sociales de Lomas de Zamora</strong>.
            </p>
          </div>
        </div>

        {/* Recuadro de registro - Primera vez */}
        <div className="mb-4 px-2">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 shadow-sm text-center">
            <p className="text-base font-bold text-gray-800 mb-2">¿Ingresás por primera vez?</p>
            <Link
              to="/register?from=auxiliares"
              className="inline-flex items-center justify-center px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white font-medium rounded-lg transition-colors"
              data-testid="register-link-top"
            >
              Registrate acá
            </Link>
          </div>
        </div>

        <Card className="shadow-lg">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl" id="login-title">Ingrese sus datos</CardTitle>
            <CardDescription>
              Sistema de Gestión del CATSLZ
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div
              className="mb-4 flex items-start gap-3 p-3 bg-blue-50 border border-blue-300 rounded-lg text-blue-900"
              role="note"
              data-testid="warning-externo-campus"
            >
              <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0 text-blue-600" aria-hidden="true" />
              <p className="text-sm leading-snug">
                <strong>¿Sos usuario externo del CATSLZ y querés entrar al Campus Virtual?</strong>{' '}
                Este login es para <strong>Auxiliares de Justicia</strong>.
                Para el Campus, ingresá desde{' '}
                <a
                  href="/campus/acceso"
                  className="font-semibold underline hover:text-blue-700"
                  data-testid="link-campus-acceso"
                >
                  este link
                </a>
                .
              </p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4" id="login-form" aria-labelledby="login-title">
              {error && (
                <div 
                  className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" 
                  data-testid="login-error"
                  role="alert"
                  aria-live="polite"
                >
                  <AlertCircle className="w-4 h-4" aria-hidden="true" />
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="input-with-icon">
                  <Mail className="icon-left w-4 h-4" aria-hidden="true" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="tu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="pl-10"
                    data-testid="login-email-input"
                    aria-describedby="email-hint"
                    autoComplete="email"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Contraseña</Label>
                <div className="input-with-icon has-right-icon">
                  <Lock className="icon-left w-4 h-4" aria-hidden="true" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="pl-10 pr-10"
                    data-testid="login-password-input"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="icon-right cursor-pointer hover:text-gray-600"
                    data-testid="toggle-password"
                    aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" aria-hidden="true" /> : <Eye className="w-4 h-4" aria-hidden="true" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
              </Button>

              <div className="text-center">
                <Link
                  to="/recuperar-password"
                  className="text-sm text-gray-500 hover:text-primary hover:underline"
                  data-testid="forgot-password-link"
                >
                  ¿Olvidaste tu contraseña?
                </Link>
              </div>
            </form>

          </CardContent>
        </Card>

        {/* iter186: Botón para matriculados de OTROS DISTRITOS (visible junto
            al login cuando las inscripciones generales están abiertas). */}
        {otrosDistritosHabilitado && (
          <Card
            className="mt-5 border-amber-300 bg-amber-50"
            data-testid="card-otros-distritos-abierta"
          >
            <CardContent className="pt-5 text-center">
              <p className="text-sm font-semibold text-amber-900 mb-3">
                ¿Sos matriculadx de otro distrito?
              </p>
              <Link to="/auxiliares-justicia/otros-distritos" className="block">
                <Button
                  className="w-full h-auto min-h-[48px] py-3 px-4 bg-amber-600 hover:bg-amber-700 text-white whitespace-normal break-words text-xs leading-snug text-center"
                  data-testid="btn-otros-distritos-ir"
                >
                  Inscripción de matriculadxs de otros distritos a los Deptos. Judiciales
                  Lomas de Zamora / Avellaneda-Lanús
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}

        <div className="text-center mt-6 mb-8 space-y-3">
          <div className="flex justify-center">
            <button 
              onClick={() => goBack()}
              className="inline-flex items-center justify-center px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
              data-testid="back-to-services-btn"
            >
              ← Volver
            </button>
          </div>
          <p className="text-xs text-gray-400 pb-4">
            Colegio de Trabajadorxs Sociales - Distrito Lomas de Zamora
          </p>
          <p className="text-[10px] text-gray-300 font-mono" data-testid="app-version">
            v{APP_VERSION} ({BUILD_ID})
          </p>
        </div>
      </div>
    </div>
  );
}
